import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useSettingsStore } from './settingsStore'

export interface PrintSettings {
  orientation: 'landscape' | 'portrait'
  pageSize: {
    height: number
    width: number
  }
  margins?: {
    marginType: 'default' | 'none' | 'printableArea' | 'custom'
    top?: number
    bottom?: number
    left?: number
    right?: number
  }
  dpi?: {
    horizontal: number
    vertical: number
  }
  /**
   * Scale factor as a percentage (10-200)
   */
  scaleFactor?: number
  printBackground?: boolean
  printResolutionScale?: number
  /**
   * Print position shift for fine-tuning alignment (in cm)
   * Adjusts @page margins independently from margin settings
   */
  printPositionShift?: {
    top?: number // in cm
    bottom?: number // in cm
    left?: number // in cm
    right?: number // in cm
  }
}

/**
 * How a print job is delivered to the printer.
 * - electron: Electron webContents.print() (default)
 * - sumatra: PDF generated then sent via pdf-to-printer (a.k.a. "printer driver defaults")
 * - hotFolder: write the composed image file into a watched folder (e.g. DNP Hot Folder
 *   Print) so the printer software handles the physical size — required for DNP DS620A
 *   panoramic (6x14 / 6x20) printing.
 */
export type PrintMethod = 'electron' | 'sumatra' | 'hotFolder'

/**
 * Configuration for the hot-folder print method.
 * The folder is a directory the printer software (e.g. DNP HFP) monitors; each
 * watched folder maps to one output size. We simply drop image files into it.
 */
export interface HotFolderConfig {
  /** Absolute path to the watched folder to drop image files into. */
  folderPath: string
}

// Store for custom settings memory
interface CustomSettingsMemory {
  orientation: 'landscape' | 'portrait'
  pageSize: {
    height: number
    width: number
  }
  dpi: {
    horizontal: number
    vertical: number
  }
}

interface SecondaryPrinterConfig {
  printerName: string
  settings: PrintSettings
  frameIds: string[] // Array of frame IDs that should use this printer
  purpose?: 'special-frames' | 'parallel' // Purpose of the printer: special frames or parallel printing
}

interface TertiaryPrinterConfig {
  printerName: string
  settings: PrintSettings
  frameIds: string[] // Array of frame IDs that should use this printer
  purpose?: 'special-frames' | 'parallel' // Purpose of the printer: special frames or parallel printing
}

interface RemotePrinterConfig {
  machineIdentifier: string // registry _id, used as selection key
  tunnelUrl: string
  printerName: string // physical printer on the remote machine (for the print payload)
  projectName?: string // friendly label shown in the dropdown
  printSettings?: PrintSettings // settings registered by the remote booth
  isAvailable: boolean
}

interface PrintState {
  status: 'idle' | 'printing' | 'error' | 'success'
  error?: string
  currentJob?: {
    printer: string
    copies: number
    images: string[]
  }
  settings: PrintSettings
  selectedPrinter?: string
  isPrintingEnabled: boolean
  useCSharpPrinting: boolean
  useSumatraPDF: boolean
  secondaryUseSumatraPDF: boolean
  tertiaryUseSumatraPDF: boolean
  // Print method per printer slot. Falls back to useSumatraPDF when unset (see getPrintMethod).
  printMethod?: PrintMethod
  secondaryPrintMethod?: PrintMethod
  tertiaryPrintMethod?: PrintMethod
  // Hot-folder config per printer slot (only used when the slot's method is 'hotFolder').
  hotFolderConfig?: HotFolderConfig
  secondaryHotFolderConfig?: HotFolderConfig
  tertiaryHotFolderConfig?: HotFolderConfig
  secondaryPrinter?: SecondaryPrinterConfig
  tertiaryPrinter?: TertiaryPrinterConfig
  customSettingsMemory?: CustomSettingsMemory
  secondaryCustomSettingsMemory?: CustomSettingsMemory
  tertiaryCustomSettingsMemory?: CustomSettingsMemory
  lastParallelPrinterIndex?: number // Track round-robin distribution for parallel printing
  remotePrinters: RemotePrinterConfig[]
  selectedPrinterTarget: 'default' | 'remote'
  selectedRemotePrinterMachineId?: string
  // Per-session override of a remote booth's registered print settings (web detailed config).
  // undefined => defer to the booth's own calibration.
  remotePrintOverride?: PrintSettings
  setStatus: (status: PrintState['status'], error?: string) => void
  setCurrentJob: (job: PrintState['currentJob']) => void
  clearJob: () => void
  updateSettings: (settings: PrintSettings) => void
  setSelectedPrinter: (printer: string | undefined) => void
  togglePrinting: () => void
  setUseCSharpPrinting: (enabled: boolean) => void
  setUseSumatraPDF: (enabled: boolean) => void
  setSecondaryUseSumatraPDF: (enabled: boolean) => void
  setTertiaryUseSumatraPDF: (enabled: boolean) => void
  getPrintMethod: (printerType?: 'default' | 'secondary' | 'tertiary') => PrintMethod
  setPrintMethod: (
    method: PrintMethod,
    printerType?: 'default' | 'secondary' | 'tertiary'
  ) => void
  getHotFolderConfig: (
    printerType?: 'default' | 'secondary' | 'tertiary'
  ) => HotFolderConfig | undefined
  setHotFolderConfig: (
    config: HotFolderConfig | undefined,
    printerType?: 'default' | 'secondary' | 'tertiary'
  ) => void
  setSecondaryPrinter: (config: SecondaryPrinterConfig | undefined) => void
  setTertiaryPrinter: (config: TertiaryPrinterConfig | undefined) => void
  getPrinterForFrame: (frameId: string) => { printerName: string; settings: PrintSettings }
  setCustomSettingsMemory: (
    memory: CustomSettingsMemory,
    printerType?: 'default' | 'secondary' | 'tertiary'
  ) => void
  getCustomSettingsMemory: (
    printerType?: 'default' | 'secondary' | 'tertiary'
  ) => CustomSettingsMemory | undefined
  getEnabledParallelPrinters: () => Array<{ printerName: string; settings: PrintSettings }>
  getNextParallelPrinter: () => { printerName: string; settings: PrintSettings } | null
  setRemotePrinters: (printers: RemotePrinterConfig[]) => void
  selectRemotePrinter: (machineIdentifier: string | undefined) => void
  selectDefaultPrinter: () => void
  setRemotePrintOverride: (settings: PrintSettings | undefined) => void
  getSelectedRemotePrinter: () => RemotePrinterConfig | null
}

// Initial state for print store. selectedPrinter is not read from settingsStore here
// to avoid hydration race; it comes from this store's own persist rehydration ('print-settings').
// When setSelectedPrinter is called, we still sync to settingsStore so both stay in sync.
const getInitialState = (): Omit<
  PrintState,
  | 'setStatus'
  | 'setCurrentJob'
  | 'clearJob'
  | 'updateSettings'
  | 'setSelectedPrinter'
  | 'togglePrinting'
  | 'setUseCSharpPrinting'
  | 'setUseSumatraPDF'
  | 'setSecondaryUseSumatraPDF'
  | 'setTertiaryUseSumatraPDF'
  | 'getPrintMethod'
  | 'setPrintMethod'
  | 'getHotFolderConfig'
  | 'setHotFolderConfig'
  | 'setSecondaryPrinter'
  | 'setTertiaryPrinter'
  | 'getPrinterForFrame'
  | 'setCustomSettingsMemory'
  | 'getCustomSettingsMemory'
  | 'getEnabledParallelPrinters'
  | 'getNextParallelPrinter'
  | 'setRemotePrinters'
  | 'selectRemotePrinter'
  | 'selectDefaultPrinter'
  | 'setRemotePrintOverride'
  | 'getSelectedRemotePrinter'
> => {
  return {
    status: 'idle' as const,
    settings: {
      orientation: 'landscape' as const,
      pageSize: {
        height: 105000,
        width: 156100
      },
      scaleFactor: 100, // Default 100%
      printResolutionScale: 1 // Default to 1x scale
    },
    selectedPrinter: undefined,
    isPrintingEnabled: true,
    useCSharpPrinting: false,
    useSumatraPDF: false,
    secondaryUseSumatraPDF: false,
    tertiaryUseSumatraPDF: false,
    remotePrinters: [],
    selectedPrinterTarget: 'default',
    remotePrintOverride: undefined
  }
}

export const usePrintStore = create<PrintState>()(
  persist(
    (set, get) => ({
      ...getInitialState(),
      setStatus: (status, error): void => void set({ status, error }),
      setCurrentJob: (currentJob): void => void set({ currentJob }),
      clearJob: (): void => void set({ currentJob: undefined, status: 'idle', error: undefined }),
      updateSettings: (settings): void => void set({ settings }),
      setSelectedPrinter: (printer): void => {
        set({ selectedPrinter: printer })
        // Sync with settings store
        useSettingsStore.getState().setSelectedPrinter(printer)
      },
      togglePrinting: (): void =>
        void set((state) => ({ isPrintingEnabled: !state.isPrintingEnabled })),
      setUseCSharpPrinting: (enabled): void => void set({ useCSharpPrinting: enabled }),
      setUseSumatraPDF: (enabled): void => void set({ useSumatraPDF: enabled }),
      setSecondaryUseSumatraPDF: (enabled): void => void set({ secondaryUseSumatraPDF: enabled }),
      setTertiaryUseSumatraPDF: (enabled): void => void set({ tertiaryUseSumatraPDF: enabled }),
      getPrintMethod: (
        printerType: 'default' | 'secondary' | 'tertiary' = 'default'
      ): PrintMethod => {
        const state = get()
        // Prefer the explicit printMethod; fall back to the legacy useSumatraPDF boolean
        // so existing booth configs keep working without migration.
        if (printerType === 'secondary') {
          return state.secondaryPrintMethod ?? (state.secondaryUseSumatraPDF ? 'sumatra' : 'electron')
        }
        if (printerType === 'tertiary') {
          return state.tertiaryPrintMethod ?? (state.tertiaryUseSumatraPDF ? 'sumatra' : 'electron')
        }
        return state.printMethod ?? (state.useSumatraPDF ? 'sumatra' : 'electron')
      },
      setPrintMethod: (
        method: PrintMethod,
        printerType: 'default' | 'secondary' | 'tertiary' = 'default'
      ): void => {
        // Keep the legacy useSumatraPDF boolean in sync so any code still reading it
        // (and SumatraPDF-specific UI gating) behaves consistently.
        const sumatra = method === 'sumatra'
        if (printerType === 'secondary') {
          set({ secondaryPrintMethod: method, secondaryUseSumatraPDF: sumatra })
        } else if (printerType === 'tertiary') {
          set({ tertiaryPrintMethod: method, tertiaryUseSumatraPDF: sumatra })
        } else {
          set({ printMethod: method, useSumatraPDF: sumatra })
        }
      },
      getHotFolderConfig: (
        printerType: 'default' | 'secondary' | 'tertiary' = 'default'
      ): HotFolderConfig | undefined => {
        const state = get()
        if (printerType === 'secondary') return state.secondaryHotFolderConfig
        if (printerType === 'tertiary') return state.tertiaryHotFolderConfig
        return state.hotFolderConfig
      },
      setHotFolderConfig: (
        config: HotFolderConfig | undefined,
        printerType: 'default' | 'secondary' | 'tertiary' = 'default'
      ): void => {
        if (printerType === 'secondary') {
          set({ secondaryHotFolderConfig: config })
        } else if (printerType === 'tertiary') {
          set({ tertiaryHotFolderConfig: config })
        } else {
          set({ hotFolderConfig: config })
        }
      },
      setSecondaryPrinter: (config): void => {
        if (config) {
          // Ensure frameIds is always a new array to prevent reference sharing
          // Default purpose to 'special-frames' for backward compatibility
          set({
            secondaryPrinter: {
              ...config,
              frameIds: [...(config.frameIds || [])],
              purpose: config.purpose || 'special-frames'
            }
          })
        } else {
          set({ secondaryPrinter: undefined })
        }
      },
      setTertiaryPrinter: (config): void => {
        if (config) {
          // Ensure frameIds is always a new array to prevent reference sharing
          // Default purpose to 'special-frames' for backward compatibility
          set({
            tertiaryPrinter: {
              ...config,
              frameIds: [...(config.frameIds || [])],
              purpose: config.purpose || 'special-frames'
            }
          })
        } else {
          set({ tertiaryPrinter: undefined })
        }
      },
      getPrinterForFrame: (frameId: string): { printerName: string; settings: PrintSettings } => {
        const state = get()
        if (state.tertiaryPrinter?.frameIds.includes(frameId)) {
          return {
            printerName: state.tertiaryPrinter.printerName,
            settings: state.tertiaryPrinter.settings
          }
        }
        if (state.secondaryPrinter?.frameIds.includes(frameId)) {
          return {
            printerName: state.secondaryPrinter.printerName,
            settings: state.secondaryPrinter.settings
          }
        }
        return {
          printerName: state.selectedPrinter || 'DS-RX1',
          settings: state.settings
        }
      },
      setCustomSettingsMemory: (
        memory: CustomSettingsMemory,
        printerType: 'default' | 'secondary' | 'tertiary' = 'default'
      ): void => {
        if (printerType === 'secondary') {
          set({ secondaryCustomSettingsMemory: memory })
        } else if (printerType === 'tertiary') {
          set({ tertiaryCustomSettingsMemory: memory })
        } else {
          set({ customSettingsMemory: memory })
        }
      },
      getCustomSettingsMemory: (
        printerType: 'default' | 'secondary' | 'tertiary' = 'default'
      ): CustomSettingsMemory | undefined => {
        const state = get()
        if (printerType === 'secondary') {
          return state.secondaryCustomSettingsMemory
        } else if (printerType === 'tertiary') {
          return state.tertiaryCustomSettingsMemory
        }
        return state.customSettingsMemory
      },
      getEnabledParallelPrinters: (): Array<{ printerName: string; settings: PrintSettings }> => {
        const state = get()
        const enabledPrinters: Array<{ printerName: string; settings: PrintSettings }> = []

        // Always include default printer for parallel printing
        if (state.selectedPrinter) {
          enabledPrinters.push({
            printerName: state.selectedPrinter,
            settings: state.settings
          })
        }

        // Include secondary printer if purpose is 'parallel'
        if (state.secondaryPrinter?.printerName && state.secondaryPrinter.purpose === 'parallel') {
          enabledPrinters.push({
            printerName: state.secondaryPrinter.printerName,
            settings: state.secondaryPrinter.settings
          })
        }

        // Include tertiary printer if purpose is 'parallel'
        if (state.tertiaryPrinter?.printerName && state.tertiaryPrinter.purpose === 'parallel') {
          enabledPrinters.push({
            printerName: state.tertiaryPrinter.printerName,
            settings: state.tertiaryPrinter.settings
          })
        }

        return enabledPrinters
      },
      getNextParallelPrinter: (): { printerName: string; settings: PrintSettings } | null => {
        const state = get()
        const enabledPrinters: Array<{ printerName: string; settings: PrintSettings }> = []

        // Always include default printer for parallel printing
        if (state.selectedPrinter) {
          enabledPrinters.push({
            printerName: state.selectedPrinter,
            settings: state.settings
          })
        }

        // Include secondary printer if purpose is 'parallel'
        if (state.secondaryPrinter?.printerName && state.secondaryPrinter.purpose === 'parallel') {
          enabledPrinters.push({
            printerName: state.secondaryPrinter.printerName,
            settings: state.secondaryPrinter.settings
          })
        }

        // Include tertiary printer if purpose is 'parallel'
        if (state.tertiaryPrinter?.printerName && state.tertiaryPrinter.purpose === 'parallel') {
          enabledPrinters.push({
            printerName: state.tertiaryPrinter.printerName,
            settings: state.tertiaryPrinter.settings
          })
        }

        if (enabledPrinters.length === 0) {
          return null
        }

        // Get or initialize last used index
        const lastIndex = state.lastParallelPrinterIndex ?? -1
        const nextIndex = (lastIndex + 1) % enabledPrinters.length

        // Update last used index
        set({ lastParallelPrinterIndex: nextIndex })

        return enabledPrinters[nextIndex]
      },
      setRemotePrinters: (printers): void => {
        set({ remotePrinters: printers })
      },
      selectRemotePrinter: (machineIdentifier): void => {
        if (machineIdentifier) {
          set({ selectedPrinterTarget: 'remote', selectedRemotePrinterMachineId: machineIdentifier })
        } else {
          set({ selectedPrinterTarget: 'default', selectedRemotePrinterMachineId: undefined })
        }
      },
      selectDefaultPrinter: (): void => {
        set({ selectedPrinterTarget: 'default', selectedRemotePrinterMachineId: undefined })
      },
      setRemotePrintOverride: (settings): void => {
        set({ remotePrintOverride: settings })
      },
      getSelectedRemotePrinter: (): RemotePrinterConfig | null => {
        const state = get()
        if (state.selectedPrinterTarget === 'remote' && state.selectedRemotePrinterMachineId) {
          const printer = state.remotePrinters.find(
            (p) => p.machineIdentifier === state.selectedRemotePrinterMachineId
          )
          return printer || null
        }
        return null
      }
    }),
    {
      name: 'print-settings',
      onRehydrateStorage: () => (state) => {
        if (state?.selectedPrinter !== undefined) {
          useSettingsStore.getState().setSelectedPrinter(state.selectedPrinter)
        }
      }
    }
  )
)
